import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { TagSelector } from 'components/TagSelector';
import { Textarea } from 'components/Textarea';
import { LINE_NAME_MAX_LENGTH, MEMO_MAX_LENGTH } from 'constants/index';
import {
  commonStrings,
  tagManagementStrings,
  userStrings,
} from 'constants/strings';
import {
  useCurrentUser,
  useLineFriend,
  useTags,
  useUpdateLineFriend,
} from 'hooks';
import { EOrganizationFeature } from 'types/enum';
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userLineId: number;
  onSuccess?: () => void;
};

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

export function EditLineFriendModal({
  isOpen,
  onClose,
  userLineId,
  onSuccess,
}: Props) {
  const { friend, loading, error } = useLineFriend(
    isOpen ? userLineId : undefined,
  );

  const { data: currentUser } = useCurrentUser();
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  );
  const {
    tags,
    loading: tagsLoading,
    error: tagsError,
  } = useTags({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
  });

  const [lineName, setLineName] = useState('');
  const [memo, setMemo] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  // タグを操作したときだけ差分を送る。ロード時点の全配列を送ると、
  // その間に他の担当者が変更したタグを巻き戻してしまう。
  const [isTagsDirty, setIsTagsDirty] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  const {
    update,
    isSubmitting,
    error: updateError,
  } = useUpdateLineFriend({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  useEffect(() => {
    if (friend) {
      setLineName(friend.lineName);
      setMemo(friend.memo ?? '');
      setSelectedTagIds(friend.tags.map((tag) => tag.id));
      setIsTagsDirty(false);
      setValidationError(undefined);
    } else {
      // 別の友だちで開き直したときに前回の値が残らないようリセットする
      setLineName('');
      setMemo('');
      setSelectedTagIds([]);
      setIsTagsDirty(false);
      setValidationError(undefined);
    }
  }, [friend]);

  const toggleTag = (tagId: number) => {
    setIsTagsDirty(true);
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  // 詳細取得が完了するまで送信不可にする。未ロードで送信すると memo=null や
  // 未ロードで送ると既存値を消し得るため、ロード完了をガードにする。
  const isLoaded = Boolean(friend);
  const isFormDisabled = loading || !isLoaded;
  const alertMessage = validationError || updateError || error;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isFormDisabled || isSubmitting) {
      return;
    }

    const normalizedLineName = lineName.trim();
    if (!normalizedLineName) {
      setValidationError(userStrings.edit.errors.required);
      return;
    }

    setValidationError(undefined);
    const normalizedMemo = normalizeOptionalText(memo);
    const initialTagIds = friend?.tags.map((tag) => tag.id) ?? [];
    const tagIdsToAdd = selectedTagIds.filter(
      (tagId) => !initialTagIds.includes(tagId),
    );
    const tagIdsToRemove = initialTagIds.filter(
      (tagId) => !selectedTagIds.includes(tagId),
    );
    // 変更したフィールドだけを送る。タグだけの更新で、その間に他の担当者が
    // 更新した名前・メモをロード時点の値へ巻き戻さない。
    const request = {
      ...(normalizedLineName !== friend?.lineName
        ? { lineName: normalizedLineName }
        : {}),
      ...(normalizedMemo !== friend?.memo ? { memo: normalizedMemo } : {}),
      ...(isTagsDirty && (tagIdsToAdd.length > 0 || tagIdsToRemove.length > 0)
        ? { tagIdsToAdd, tagIdsToRemove }
        : {}),
    };
    if (Object.keys(request).length === 0) {
      onClose();
      return;
    }

    update(userLineId, request);
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <form
        className="grid gap-5"
        data-testid="edit-line-friend-form"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-lg font-semibold text-ink">
            {userStrings.edit.form.title}
          </h2>
          <p className="text-sm text-muted">
            {userStrings.edit.form.description}
          </p>
        </div>

        {alertMessage && <Alert>{alertMessage}</Alert>}

        <div className="grid gap-1">
          <span className="text-xs font-semibold text-muted">
            {userStrings.edit.summary.labels.originalLineName}
          </span>
          <span className="text-sm text-ink">
            {friend?.originalLineName || userStrings.edit.summary.emptyValue}
          </span>
        </div>

        <label className="grid gap-2 text-xs font-semibold text-ink">
          <span>
            {userStrings.edit.form.fields.lineName.label}
            <span className="ml-1 text-danger">{commonStrings.required}</span>
          </span>
          <Input
            name="lineName"
            placeholder={userStrings.edit.form.fields.lineName.placeholder}
            autoComplete="off"
            required
            maxLength={LINE_NAME_MAX_LENGTH}
            disabled={isFormDisabled || isSubmitting}
            value={lineName}
            onChange={(event) => setLineName(event.target.value)}
          />
        </label>

        <label className="grid gap-2 text-xs font-semibold text-ink">
          <span>{userStrings.edit.form.fields.memo.label}</span>
          <Textarea
            name="memo"
            placeholder={userStrings.edit.form.fields.memo.placeholder}
            maxLength={MEMO_MAX_LENGTH}
            disabled={isFormDisabled || isSubmitting}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />
          <span className="text-[11px] font-medium text-muted">
            {userStrings.edit.form.fields.memo.helper} ({memo.length}/
            {MEMO_MAX_LENGTH})
          </span>
        </label>

        {canUseTags && (
          <div className="grid gap-2 text-xs font-semibold text-ink">
            <span>{tagManagementStrings.editForFriend.title}</span>
            {tagsLoading ? (
              <p className="text-[11px] font-medium text-muted">
                {commonStrings.loading}
              </p>
            ) : tagsError ? (
              // 取得失敗を「タグが無い」と誤認させない（タグ0件と区別する）
              <p className="text-[11px] font-medium text-danger">{tagsError}</p>
            ) : tags.length === 0 ? (
              <p className="text-[11px] font-medium text-muted">
                {tagManagementStrings.editForFriend.empty}
              </p>
            ) : (
              <TagSelector
                tags={tags}
                selectedTagIds={selectedTagIds}
                onToggle={toggleTag}
                disabled={isFormDisabled || isSubmitting}
              />
            )}
          </div>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={handleClose}
          >
            {commonStrings.cancel}
          </Button>
          <Button
            type="submit"
            className="flex-1"
            loading={isSubmitting}
            disabled={isFormDisabled || isSubmitting}
          >
            {commonStrings.actions.update}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
